import { Flex } from 'antd';
import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import {
  Button,
  ButtonVariant,
  ConditionView,
  FormInputPhone,
  Icon,
  Informer,
  StepControlPanel,
  useModal,
} from '@/shared/ui';

import { Timer } from '@/components/Timer';
import { VerificationField } from '@/components/VerificationInput';

import { UserRequestDraft } from '@/api/types';

import api from '@/api';

import { STEP_NUMBER_TO_EDIT_PHONE } from '../../lib/constants';
import { InsuranceServiceError, InsuranceServiceMergedSchema } from '../../model/types';
import { InsurancePhoneChangeModal } from '../InsurancePhoneChangeModal';
import { InsuranceServiceErrorInformer } from '../InsuranceServiceErrorInformer';

export interface SmsVerificationProps {
  userRequestDraft: UserRequestDraft;
}

export const SmsVerification: React.FC<StepProps<SmsVerificationProps>> = ({
  userRequestDraft,
  specificStep,
  next,
  prev,
}) => {
  const { control } = useFormContext<InsuranceServiceMergedSchema>();
  const { isOpen, openModal, closeModal } = useModal();

  const initialAttemptsCount = 5;
  const otpLength = 4;

  const [insuranceServiceError, setInsuranceServiceError] = useState<Nullable<InsuranceServiceError>>(null);

  const [isOtpValidationSucceeded, setIsOtpValidationSucceeded] = useState<boolean>(false);
  const [timeoutId, setTimeoutId] = useState<Nullable<NodeJS.Timeout>>(null);

  const [isOtpGenerating, setIsOtpGenerating] = useState<boolean>(false);
  const [isOtpTimerLoading, setIsOtpTimerLoading] = useState<boolean>(false);

  const [validationAttemptsLeft, setValidationAttemptsLeft] = useState<number>(initialAttemptsCount);
  const [validationTimeLeft, setValidationTimeLeft] = useState<number>(0);
  const [generationOtpCounter, setGenerationOtpCounter] = useState<number>(0);
  const [generationOtpBanInSeconds, setGenerationOtpBanInSeconds] = useState<number>(0);

  const updateOtpTimer = useCallback(async () => {
    if (!isEmpty(timeoutId)) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }

    setIsOtpTimerLoading(true);

    const otpTimerData = await api.bia.otpTimer(`${userRequestDraft.id}`);

    if (otpTimerData) {
      const { validation_attempts, validation_timer, submission_attempts, submission_timer } = otpTimerData.data;

      setValidationAttemptsLeft(validation_attempts);
      setValidationTimeLeft(validation_timer);
      setGenerationOtpBanInSeconds(submission_timer);
      setGenerationOtpCounter(submission_attempts);

      if ([validation_timer, submission_timer].some((timer) => timer > 0) && !isOtpValidationSucceeded) {
        const timeout =
          submission_timer === 0
            ? validation_timer
            : validation_timer === 0
              ? submission_timer
              : Math.min(submission_timer, validation_timer);

        const id = setTimeout(updateOtpTimer, timeout * 1000);
        setTimeoutId(id);
      }
    }

    setIsOtpTimerLoading(false);
  }, [userRequestDraft.id, isOtpValidationSucceeded, timeoutId]);

  const generateOtp = async (): Promise<void> => {
    setIsOtpGenerating(true);

    await api.bia
      .generateOtp({
        request_id: `${userRequestDraft.id}`,
        user_accepted_terms: true, // confirmed in modal
      })
      .then(updateOtpTimer)
      .catch(async (response) => {
        switch (JSON.parse(response)?.responseStatus) {
          case 409:
            await updateOtpTimer();
            break;

          case 502:
            setInsuranceServiceError(InsuranceServiceError.REQUEST_FAILED);
            break;

          default:
            console.error(response);
            break;
        }
      })
      .finally(() => setIsOtpGenerating(false));
  };

  const validateOtp = useCallback(
    async (otp: string): Promise<boolean> => {
      if (otp.length > otpLength) {
        throw new Error(`OTP length is more then ${otpLength}`);
      }

      setIsOtpGenerating(true);

      const isOtpValid = await api.bia
        .validateOtp(`${userRequestDraft.id}`, otp)
        .then(() => {
          setIsOtpValidationSucceeded(true);
          timeoutId && clearTimeout(timeoutId);
          setTimeoutId(null);
          return true;
        })
        .catch(async (response) => {
          switch (JSON.parse(response)?.responseStatus) {
            case 400:
            case 409:
              await updateOtpTimer();
              break;

            case 502:
              setInsuranceServiceError(InsuranceServiceError.REQUEST_FAILED);
              break;

            default:
              console.error(response);
              break;
          }

          return false;
        })
        .finally(() => setIsOtpGenerating(false));

      return isOtpValid;
    },
    [userRequestDraft.id, otpLength, timeoutId, updateOtpTimer],
  );

  useEffect(() => {
    updateOtpTimer();
  }, []);

  return (
    <>
      <ConditionView
        if={!insuranceServiceError}
        else={
          <>
            <FormBlock title="Верифікація за СМС">
              <InsuranceServiceErrorInformer error={insuranceServiceError} />
            </FormBlock>

            <StepControlPanel
              next={() => {
                setInsuranceServiceError(null);
              }}
              nextBtnText="Спробувати ще"
              withArrow={false}
            />
          </>
        }
      >
        <FormBlock title="Верифікація за СМС">
          <div className="sms-verification-step__data-preview">
            <div>СМС буде відправлено на номер телефону</div>
            <div className="sms-verification-step__data-preview-content">
              <FormInputPhone name="phone" control={control} disabled readOnly aria-readonly label="" />

              <InsurancePhoneChangeModal
                isOpen={isOpen}
                onClose={closeModal}
                onContinue={() => specificStep(STEP_NUMBER_TO_EDIT_PHONE)}
              />
              <Button variant={ButtonVariant.SECONDARY} onClick={openModal} disabled={isOtpValidationSucceeded}>
                Редагувати
              </Button>
            </div>
          </div>

          <ConditionView if={generationOtpCounter > 0}>
            <ConditionView if={!isOtpValidationSucceeded}>
              <Informer
                icon={{
                  name: 'info',
                  color: 'blue',
                }}
                text={{
                  text: (
                    <>
                      На вказаний вами номер телефону відправлено СМС з кодом підтвердження. <br />
                      Введіть цей код в поле нижче.
                    </>
                  ),
                  color: 'black',
                }}
                borderColor="blue"
              />
            </ConditionView>

            <VerificationField
              label="Код із СМС"
              disabled={
                validationTimeLeft === 0 || validationAttemptsLeft === 0 || isOtpGenerating || isOtpTimerLoading
              }
              inputQuantity={otpLength}
              attemptsLeft={validationAttemptsLeft}
              initialAttempts={initialAttemptsCount}
              validateValue={validateOtp}
            />
          </ConditionView>

          <ConditionView if={!isOtpValidationSucceeded}>
            <ConditionView if={generationOtpBanInSeconds === 0}>
              <div>
                <Button onClick={generateOtp}>
                  <Flex gap={8}>
                    <Icon name="send" color="white" />
                    Відправити СМС
                  </Flex>
                </Button>
              </div>
            </ConditionView>

            <ConditionView if={generationOtpBanInSeconds > 0}>
              <Informer
                borderColor="blue"
                icon={{ name: 'info', color: 'blue' }}
                header={{ text: 'Зверніть увагу!', color: 'blue' }}
                text={{
                  color: 'black',
                  text: (
                    <ConditionView
                      if={validationAttemptsLeft > 0}
                      else={
                        <Flex gap={4}>
                          Відправити нову CМС можна через <Timer seconds={generationOtpBanInSeconds} />
                        </Flex>
                      }
                    >
                      <Flex gap={4}>
                        Не прийшла СМС? Відправити нову можна через <Timer seconds={generationOtpBanInSeconds} />
                      </Flex>
                    </ConditionView>
                  ),
                }}
              />
            </ConditionView>
          </ConditionView>
        </FormBlock>

        <StepControlPanel
          prev={prev}
          disabledPrev={isOtpValidationSucceeded}
          next={next}
          disabledNext={!isOtpValidationSucceeded}
        />
      </ConditionView>
    </>
  );
};
